import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import AnalyticsContextBanner from './AnalyticsContextBanner';
import './ReliabilityMetrics.css';
import RiskScoreInfoModal from '../../../shared/components/common/RiskScoreInfoModal';
import DeviceReliabilityHelpModal from '../../../shared/components/common/DeviceReliabilityHelpModal';
import ContextHelpButton from '../../../shared/components/common/ContextHelpButton';
import ComplaintView from '../../complaints/components/ComplaintView';
import { ComplaintIDBadge, StatusBadge, SeverityBadge } from '../../../shared/components/common/Badges';
import { API_ENDPOINTS } from '../../../config/apiConfig';

const ACTIVE_COMPLAINT_STATUSES = ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'];

const CATEGORY_CHART_COLORS = [
  '#6366f1',
  '#0d9488',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#3b82f6',
  '#14b8a6',
  '#f97316',
];

function normalizeHealthReasonChip(reason) {
  if (!reason) return '';
  return reason.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/** Assignment label for device complaint history (matches DocumentManager "You" / name pattern). */
function getComplaintAssignmentMeta(complaint, user) {
  const assignedId = complaint?.assigned_employee_id;
  const assignedName = complaint?.assigned_to_name;
  const myEmployeeId = user?.employee_id;

  if (!assignedId) {
    return { kind: 'unassigned', label: 'Unassigned', isMine: false };
  }

  const isMine =
    myEmployeeId != null && String(assignedId) === String(myEmployeeId);

  if (isMine) {
    return { kind: 'mine', label: 'You', isMine: true };
  }

  return {
    kind: 'other',
    label: assignedName || String(assignedId),
    isMine: false,
  };
}

function getHealthChipClassName(reason, healthStatus) {
  const text = reason.toLowerCase();
  if (
    text.includes('critical')
    || text.includes('overdue')
    || (text.includes('availability') && text.includes('%'))
    || text.includes('downtime')
  ) {
    return 'health-reason-chip health-reason-chip--critical';
  }
  if (healthStatus === 'at_risk' || text.includes('major') || text.includes('impacting')) {
    return 'health-reason-chip health-reason-chip--warning';
  }
  return 'health-reason-chip health-reason-chip--neutral';
}

const METRICS_DEFAULTS = {
  availability_percentage: 0,
  reliability_score: 0,
  total_complaints: 0,
  open_complaints: 0,
  acknowledged_complaints: 0,
  in_progress_complaints: 0,
  escalated_to_vendor_complaints: 0,
  closed_complaints: 0,
  resolved_downtime_days: 0,
  resolved_downtime_hours: 0,
  current_downtime_days: 0,
  current_downtime_hours: 0,
  mtbf_days: 0,
  mtbf_hours: 0,
  last_complaint_date: null,
  last_complaint: null,
  device_age_years: 0,
  device_age_days: 0,
  avg_resolution_hours: 0,
  complaint_distribution: [],
  complaints_by_category: [],
  complaint_history: [],
  risk_score: 3,
  risk_factors: {},
  ppm_status: 'compliant',
  calibration_status: 'compliant',
  warranty_status: 'active',
  contract_status: 'active',
  complaints_by_severity: { critical: 0, major: 0, minor: 0 },
  health_status: 'healthy',
  health_reasons: [],
  observation_window: { label: 'Last 12 months' },
  avg_restore_hours: null,
  current_downtime_hours_live: null,
  failures_count_12mo: 0,
  closed_complaints_12mo: 0,
  total_complaints_12mo: 0,
  resolved_downtime_hours_total: 0,
  active_complaints: [],
  active_complaints_by_severity: { critical: 0, major: 0, minor: 0 },
  active_complaints_count: 0,
};

function ReliabilityMetrics({ deviceId, token, readOnlyReviewMode = false, user = null, refreshKey = 0 }) {
  const [operationalMetrics, setOperationalMetrics] = useState(null);
  const [lifetimeMetrics, setLifetimeMetrics] = useState(null);
  const [hasLifetimeHistory, setHasLifetimeHistory] = useState(false);
  const [metricsView, setMetricsView] = useState('operational');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandComplaintHistory, setExpandComplaintHistory] = useState(false);
  const [showRiskInfo, setShowRiskInfo] = useState(false);
  const [showReliabilityHelp, setShowReliabilityHelp] = useState(false);
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [selectedComplaintViewOnly, setSelectedComplaintViewOnly] = useState(false);

  useEffect(() => {
    fetchMetrics(true);
  }, [deviceId, readOnlyReviewMode]);

  useEffect(() => {
    if (refreshKey > 0) {
      fetchMetrics(false);
    }
  }, [refreshKey]);

  const isTechnician = user?.role === 'technician';

  const openComplaintFromHistory = (complaint) => {
    const meta = getComplaintAssignmentMeta(complaint, user);
    // Technicians may view peer/unassigned complaints on their devices for context only
    const viewOnly =
      readOnlyReviewMode || (isTechnician && !meta.isMine);
    setSelectedComplaintViewOnly(viewOnly);
    setSelectedComplaintId(complaint.id);
  };

  const closeComplaintModal = () => {
    setSelectedComplaintId(null);
    setSelectedComplaintViewOnly(false);
  };

  const activeMetrics = useMemo(() => {
    if (!operationalMetrics) return null;
    if (readOnlyReviewMode) return operationalMetrics;
    if (metricsView === 'lifetime' && lifetimeMetrics) return lifetimeMetrics;
    return operationalMetrics;
  }, [operationalMetrics, lifetimeMetrics, metricsView, readOnlyReviewMode]);

  useEffect(() => {
    if ((activeMetrics?.active_complaints_count || 0) > 0) {
      setExpandComplaintHistory(true);
    }
  }, [activeMetrics?.active_complaints_count]);

  const fetchMetrics = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError('');

    try {
      if (!deviceId) {
        setError('Device ID not provided');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_ENDPOINTS.DEVICES.RELIABILITY_METRICS(deviceId)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: readOnlyReviewMode ? { for_transfer_review: true } : {},
        }
      );

      const operational = response.data.operational || response.data;
      const operationalData = { ...METRICS_DEFAULTS, ...operational };
      setOperationalMetrics(operationalData);
      setLifetimeMetrics(
        response.data.lifetime ? { ...METRICS_DEFAULTS, ...response.data.lifetime } : null
      );
      setHasLifetimeHistory(!!response.data.has_lifetime_history);
      setMetricsView('operational');
    } catch (err) {
      console.error('Error fetching reliability metrics:', err);
      console.error('Device ID:', deviceId);
      console.error('Error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load reliability metrics';
      setError(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#27ae60'; // Green
    if (score >= 60) return '#f39c12'; // Orange
    if (score >= 40) return '#e74c3c'; // Red
    return '#c0392b'; // Dark Red
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  const getRiskColor = (score) => {
    if (score >= 7) return '#e74c3c'; // Red - Critical
    if (score >= 5) return '#f39c12'; // Orange - Warning
    return '#27ae60'; // Green - Low
  };

  const getRiskLabel = (score) => {
    if (score >= 7) return 'Critical';
    if (score >= 5) return 'Warning';
    return 'Low';
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return '#27ae60';
      case 'at_risk':
        return '#f39c12';
      case 'critical':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const getHealthStatusLabel = (status) => {
    switch (status) {
      case 'healthy':
        return 'Healthy';
      case 'at_risk':
        return 'At Risk';
      case 'critical':
        return 'Critical';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant':
        return '#27ae60';
      case 'due_soon':
        return '#f39c12';
      case 'overdue':
        return '#e74c3c';
      case 'active':
        return '#27ae60';
      case 'expired':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeValue = (days, hours, unit = 'days') => {
    if (unit === 'days') {
      if (days === 0 && hours === 0) return '0 d';
      if (days === 0) return `${hours}h`;
      if (hours === 0) return `${days}d`;
      return `${days}d ${hours}h`;
    } else if (unit === 'years') {
      if (days === 0) return '0 y';
      if (hours === 0) return `${days}y`;
      return `${days}y ${hours}d`;
    }
    return '0';
  };

  const formatHoursAsDuration = (totalHours, showNa = false) => {
    if (showNa || totalHours === null || totalHours === undefined) return 'N/A';
    const hours = Number(totalHours);
    if (Number.isNaN(hours) || hours <= 0) return '0 d';
    return formatTimeValue(Math.floor(hours / 24), Math.round(hours % 24), 'days');
  };

  const formatMtbf = (m) => {
    if (m.mtbf_days == null && m.mtbf_days_float == null) return 'N/A';
    const totalDays = m.mtbf_days_float != null
      ? m.mtbf_days_float
      : (m.mtbf_days || 0) + (m.mtbf_hours || 0) / 24;
    if (totalDays <= 0) return 'N/A';
    return formatTimeValue(Math.floor(totalDays), Math.round((totalDays % 1) * 24), 'days');
  };

  if (loading) {
    return <div className="reliability-metrics-container"><div className="loading-state">Loading reliability metrics...</div></div>;
  }

  if (error || !activeMetrics) {
    return <div className="reliability-metrics-container"><div className="error-state">{error || 'Unable to load metrics'}</div></div>;
  }

  const metrics = activeMetrics;

  const scoreColor = getScoreColor(metrics.reliability_score);
  const scoreLabel = getScoreLabel(metrics.reliability_score);
  const riskColor = getRiskColor(metrics.risk_score);
  const riskLabel = getRiskLabel(metrics.risk_score);
  const healthColor = getHealthStatusColor(metrics.health_status);
  const healthLabel = getHealthStatusLabel(metrics.health_status);

  const activeComplaints = (metrics.active_complaints && metrics.active_complaints.length > 0)
    ? metrics.active_complaints
    : (metrics.complaint_history || []).filter((c) => ACTIVE_COMPLAINT_STATUSES.includes(c.status));

  const activeBySeverity = metrics.active_complaints_by_severity || {
    critical: activeComplaints.filter((c) => (c.severity || 'minor') === 'critical').length,
    major: activeComplaints.filter((c) => (c.severity || 'minor') === 'major').length,
    minor: activeComplaints.filter((c) => (c.severity || 'minor') === 'minor').length,
  };

  const hasActiveComplaints = activeComplaints.length > 0;
  const activeCount = metrics.active_complaints_count ?? activeComplaints.length;
  const assignedToMeActiveCount = isTechnician
    ? activeComplaints.filter((c) => {
        const meta = getComplaintAssignmentMeta(c, user);
        return meta.isMine;
      }).length
    : 0;
  const otherActiveCount = isTechnician
    ? Math.max(0, activeCount - assignedToMeActiveCount)
    : 0;
  const operationalHistory = operationalMetrics?.complaint_history || [];
  const complaintHistoryRows = readOnlyReviewMode
    ? (metrics.complaint_history || [])
    : operationalHistory;
  const showComplaintHistorySection =
    readOnlyReviewMode
      ? complaintHistoryRows.length > 0
      : metricsView === 'operational' && operationalHistory.length > 0;
  const healthChips = (metrics.health_reasons || [])
    .map(normalizeHealthReasonChip)
    .filter(Boolean);

  const totalComplaints12mo = metrics.total_complaints_12mo ?? (
    (metrics.complaint_distribution || []).reduce((sum, m) => sum + (m.count || 0), 0)
  );

  return (
    <div className="reliability-metrics-container">
      <div className="metrics-header">
        <div className="metrics-header-title-row">
          <h3 style={{ margin: 0 }}>Reliability Metrics &amp; Analytics</h3>
          <ContextHelpButton
            onClick={() => setShowReliabilityHelp(true)}
            title="How are reliability metrics calculated?"
          />
        </div>
        <p className="metrics-subtitle">
          {readOnlyReviewMode
            ? 'Full device history for transfer review'
            : metricsView === 'lifetime'
              ? 'Lifetime performance across all locations'
              : 'Performance at this location'}
        </p>
        {!readOnlyReviewMode && hasLifetimeHistory && (
          <div className="reliability-metrics-scope-toggle" role="tablist" aria-label="Metrics scope">
            <button
              type="button"
              role="tab"
              aria-selected={metricsView === 'operational'}
              className={`reliability-scope-btn${metricsView === 'operational' ? ' reliability-scope-btn--active' : ''}`}
              onClick={() => setMetricsView('operational')}
            >
              Operational
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={metricsView === 'lifetime'}
              className={`reliability-scope-btn${metricsView === 'lifetime' ? ' reliability-scope-btn--active' : ''}`}
              onClick={() => setMetricsView('lifetime')}
            >
              Lifetime
            </button>
          </div>
        )}
      </div>

      <AnalyticsContextBanner
        variant="window"
        title={metrics.observation_window?.label || 'Last 12 months'}
      >
        {readOnlyReviewMode
          ? 'Includes all complaints on this device before and after any transfer.'
          : metricsView === 'lifetime'
            ? 'Includes complaint history from prior locations. Maintenance and device age reflect the current asset record.'
            : 'Complaint rates reflect this location only. Use Lifetime for full device history across sites.'}
      </AnalyticsContextBanner>

      {/* Health Status Indicator - Top Priority */}
      <div className="health-status-banner" style={{ borderLeftColor: healthColor }}>
        <div className="health-status-banner-main">
          <span className="health-status-icon">
            {metrics.health_status === 'healthy' ? '✅' : metrics.health_status === 'at_risk' ? '⚠️' : '🔴'}
          </span>
          <div className="health-status-text">
            <div className="health-status-label">Device Health Status</div>
            <div className="health-status-value" style={{ color: healthColor }}>
              {healthLabel}
            </div>
          </div>
        </div>
        {healthChips.length > 0 ? (
          <div className="health-reason-chips" role="list">
            {healthChips.map((reason, idx) => (
              <span
                key={idx}
                role="listitem"
                className={getHealthChipClassName(reason, metrics.health_status)}
              >
                {reason}
              </span>
            ))}
          </div>
        ) : (
          <p className="health-status-summary">
            {metrics.health_status === 'healthy' && 'Device is operating normally with no critical issues'}
            {metrics.health_status === 'at_risk' && 'Device has some concerns that need attention'}
            {metrics.health_status === 'critical' && 'Device requires immediate attention'}
          </p>
        )}
      </div>

      {/* Metrics Grid - All metrics in single grid */}
      <div className="metrics-grid-all">
        {/* Reliability Score */}
        <div className="metric-card score-card reliability-score-card">
          <div className="metric-label">Reliability Score</div>
          <div className="score-content">
            <div className="score-main">
              <div className="metric-score-circle" style={{ borderColor: scoreColor, backgroundColor: `${scoreColor}15` }}>
                <div className="score-fraction">
                  <div className="metric-score-value" style={{ color: scoreColor }}>
                    {metrics.reliability_score.toFixed(0)}
                  </div>
                  <div className="score-divider-line"></div>
                  <div className="metric-score-max">100</div>
                </div>
              </div>
              <div className="score-info">
                <div className="metric-score-label" style={{ color: scoreColor }}>
                  {scoreLabel}
                </div>
                <div className="score-description">
                  {scoreColor === '#27ae60' && 'Excellent reliability'}
                  {scoreColor === '#f39c12' && 'Good reliability'}
                  {scoreColor === '#e74c3c' && 'Needs attention'}
                  {scoreColor === '#c0392b' && 'Critical issues'}
                  {metrics.health_status === 'critical' && (
                    <span className="score-health-note"> (capped by critical health)</span>
                  )}
                </div>
              </div>
            </div>
            <div className="score-bar">
              <div className="score-bar-fill" style={{ width: `${metrics.reliability_score}%`, backgroundColor: scoreColor }}></div>
            </div>
          </div>
        </div>

        {/* Risk Score Assessment */}
        <div className="metric-card risk-score-card">
          <div className="metric-label-with-btn">
            <span>Risk Score</span>
            <ContextHelpButton
              onClick={() => setShowRiskInfo(!showRiskInfo)}
              title="How is risk score calculated?"
            />
          </div>
          <div className="score-content">
            <div className="score-main">
              <div className="metric-score-circle" style={{ borderColor: riskColor, backgroundColor: `${riskColor}15` }}>
                <div className="score-fraction">
                  <div className="metric-score-value" style={{ color: riskColor }}>
                    {metrics.risk_score}
                  </div>
                  <div className="score-divider-line"></div>
                  <div className="metric-score-max">9</div>
                </div>
              </div>
              <div className="score-info">
                <div className="metric-score-label" style={{ color: riskColor }}>
                  {riskLabel}
                </div>
                <div className="score-description">
                  {riskColor === '#27ae60' && 'Low risk level'}
                  {riskColor === '#f39c12' && 'Monitor closely'}
                  {riskColor === '#e74c3c' && 'Immediate action'}
                </div>
              </div>
            </div>
            <div className="risk-factors">
              <div className="risk-factor-item" style={{ opacity: metrics.risk_factors.ppm_overdue ? 1 : 0.4 }}>
                <span className="risk-factor-icon">🔧</span>
                <span className="risk-factor-label">Maintenance</span>
              </div>
              <div className="risk-factor-item" style={{ opacity: metrics.risk_factors.complaint_risk ? 1 : 0.4 }}>
                <span className="risk-factor-icon">⚠️</span>
                <span className="risk-factor-label">Complaints</span>
              </div>
              <div className="risk-factor-item" style={{ opacity: metrics.risk_factors.age_risk ? 1 : 0.4 }}>
                <span className="risk-factor-icon">📅</span>
                <span className="risk-factor-label">Device&nbsp;Age</span>
              </div>
            </div>
          </div>
        </div>

        {/* Availability Percentage */}
        <div className="metric-card metric-card--availability">
          <div className="metric-label">Availability %</div>
          <div className="metric-value" style={{ color: (metrics.availability_percentage || 0) >= 95 ? '#27ae60' : (metrics.availability_percentage || 0) >= 80 ? '#f39c12' : '#e74c3c' }}>
            {(metrics.availability_percentage !== null && metrics.availability_percentage !== undefined) ? metrics.availability_percentage.toFixed(1) : '0.0'}%
          </div>
          <div className="metric-description">
            Uptime in last 12 months
          </div>
        </div>

        {/* Complaints — severity columns + 12 mo total stack */}
        <div className="metric-card complaints-summary-card">
          <div className="metric-label">Complaints</div>
          <div className="complaints-compact-main">
            <div
              className="complaints-severity-grid"
              aria-label={`Active complaints: ${activeBySeverity.critical || 0} critical, ${activeBySeverity.major || 0} major, ${activeBySeverity.minor || 0} minor`}
            >
              <div className="complaints-severity-col complaints-severity-col--critical">
                <span className="complaints-severity-value">{activeBySeverity.critical || 0}</span>
                <span className="complaints-severity-label">Critical</span>
              </div>
              <div className="complaints-severity-col complaints-severity-col--major">
                <span className="complaints-severity-value">{activeBySeverity.major || 0}</span>
                <span className="complaints-severity-label">Major</span>
              </div>
              <div className="complaints-severity-col complaints-severity-col--minor">
                <span className="complaints-severity-value">{activeBySeverity.minor || 0}</span>
                <span className="complaints-severity-label">Minor</span>
              </div>
            </div>
            <div className="complaints-total-stack" aria-label={`${totalComplaints12mo} complaints opened in last 12 months`}>
              <span className="complaints-total-value">{totalComplaints12mo}</span>
              <span className="complaints-total-label">Opened</span>
              <span className="complaints-total-sublabel">12 mo</span>
            </div>
          </div>
          {hasActiveComplaints && showComplaintHistorySection && (
            <div className="metric-description">active now · history below</div>
          )}
        </div>

        {/* Resolved Downtime */}
        <div className="metric-card">
          <div className="metric-label">Resolved downtime (12 mo)</div>
          <div className="metric-value">
            {formatHoursAsDuration(
              metrics.resolved_downtime_hours_total,
              (metrics.closed_complaints_12mo || 0) === 0 && (metrics.resolved_downtime_hours_total || 0) === 0
            )}
          </div>
          <div className="metric-description">Closed impacting complaints in window</div>
        </div>

        {/* Current Downtime */}
        <div className="metric-card">
          <div className="metric-label">Current downtime (live)</div>
          <div className="metric-value">
            {formatHoursAsDuration(
              metrics.current_downtime_hours_live != null
                ? metrics.current_downtime_hours_live
                : (metrics.current_downtime_days || 0) * 24 + (metrics.current_downtime_hours || 0)
            )}
          </div>
          <div className="metric-description">Active impacting outages now</div>
        </div>

        {/* MTBF */}
        <div className="metric-card">
          <div className="metric-label">MTBF (12 mo)</div>
          <div className="metric-value">{formatMtbf(metrics)}</div>
          <div className="metric-description">Mean time between failures</div>
        </div>

        {/* Device Age */}
        <div className="metric-card">
          <div className="metric-label">Device Age</div>
          <div className="metric-value">{formatTimeValue(metrics.device_age_years, metrics.device_age_days, 'years')}</div>
          <div className="metric-description">Time in service</div>
        </div>

        {/* Average Resolution Time */}
        <div className="metric-card">
          <div className="metric-label">Avg. restore time (12 mo)</div>
          <div className="metric-value">
            {formatHoursAsDuration(
              metrics.avg_restore_hours != null ? metrics.avg_restore_hours : metrics.avg_resolution_hours,
              metrics.avg_restore_hours == null && (!metrics.avg_resolution_hours || metrics.avg_resolution_hours === 0)
            )}
          </div>
          <div className="metric-description">Breakdown to closed (impacting)</div>
        </div>

        {/* Summary Cards in same grid */}
        {(() => {
          // Calculate complaint insights
          const complaintsData = metrics.complaint_distribution || [];
          const monthsWithComplaints = complaintsData.filter(m => m.count > 0);
          const peakMonth = complaintsData.reduce((max, m) => m.count > max.count ? m : max, { month: 'N/A', count: 0 });
          const recentMonths = complaintsData.slice(-6);
          const olderMonths = complaintsData.slice(0, 6);
          const recentTotal = recentMonths.reduce((sum, m) => sum + m.count, 0);
          const olderTotal = olderMonths.reduce((sum, m) => sum + m.count, 0);
          const trendDirection = recentTotal < olderTotal ? 'improving' : recentTotal > olderTotal ? 'worsening' : 'stable';
          const trendPercent = olderTotal > 0 ? Math.abs(((recentTotal - olderTotal) / olderTotal) * 100).toFixed(0) : 0;

          return (
            <>
              {/* Complaint Activity Summary */}
              <div className="summary-card">
                <div className="summary-label">Complaint Activity</div>
                <div className="summary-content">
                  <div className="summary-stat-row">
                    <span className="summary-stat-name">Months Active:</span>
                    <span className="summary-stat-value">{monthsWithComplaints.length}/12</span>
                  </div>
                  <div className="summary-stat-row">
                    <span className="summary-stat-name">Worst Month:</span>
                    <span className="summary-stat-value">{peakMonth.month}</span>
                  </div>
                  <div className="summary-stat-row">
                    <span className="summary-stat-name">Recent vs Older:</span>
                    <span className="summary-stat-value">{recentTotal} vs {olderTotal}</span>
                  </div>
                </div>
              </div>

              {/* Trend Indicator */}
              <div className="summary-card trend-card">
                <div className="summary-label">Trend</div>
                <div className="trend-indicator" data-trend={trendDirection}>
                  <span className="trend-icon">
                    {trendDirection === 'improving' ? '📈' : trendDirection === 'worsening' ? '📉' : '➡️'}
                  </span>
                  <div className="trend-text">
                    <div className="trend-status">
                      {trendDirection === 'improving' ? 'Improving' : trendDirection === 'worsening' ? 'Worsening' : 'Stable'}
                    </div>
                    <div className="trend-detail">{trendPercent}% {trendDirection === 'improving' ? 'decrease' : trendDirection === 'worsening' ? 'increase' : 'no change'}</div>
                  </div>
                </div>
              </div>

              {/* Resolution Efficiency */}
              <div className="summary-card">
                <div className="summary-label">Resolution</div>
                <div className="summary-content">
                  <div className="summary-stat-row">
                    <span className="summary-stat-name">Avg.:</span>
                    <span className="summary-stat-value">{(metrics.avg_resolution_hours / 24).toFixed(1)}d</span>
                  </div>
                  <div className="summary-stat-row">
                    <span className="summary-stat-name">Closed:</span>
                    <span className="summary-stat-value">{metrics.closed_complaints}/{metrics.total_complaints}</span>
                  </div>
                  <div className="summary-stat-row">
                    <span className="summary-stat-name">Open:</span>
                    <span className="summary-stat-value">
                      {(metrics.complaint_history || []).filter((c) => ACTIVE_COMPLAINT_STATUSES.includes(c.status)).length}
                    </span>
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Maintenance & Compliance Status */}
      <div className="reliability-section">
        <div className="reliability-section-header">
          <h4>Maintenance & Compliance Status</h4>
          <p className="reliability-section-desc">Current maintenance and warranty status</p>
        </div>
        <div className="maintenance-grid">
          {/* PPM Status */}
          <div className="maintenance-card">
            <div className="maintenance-label">PPM Status</div>
            <div className="maintenance-status" style={{ color: getStatusColor(metrics.ppm_status) }}>
              {metrics.ppm_status === 'compliant' && '✓ Compliant'}
              {metrics.ppm_status === 'due_soon' && '⚠ Due Soon'}
              {metrics.ppm_status === 'overdue' && '✗ Overdue'}
            </div>
            {metrics.ppm_next_due_date && (
              <div className="maintenance-date">Next: {formatDate(metrics.ppm_next_due_date)}</div>
            )}
          </div>

          {/* Calibration Status */}
          <div className="maintenance-card">
            <div className="maintenance-label">Calibration Status</div>
            <div className="maintenance-status" style={{ color: getStatusColor(metrics.calibration_status) }}>
              {metrics.calibration_status === 'compliant' && '✓ Compliant'}
              {metrics.calibration_status === 'due_soon' && '⚠ Due Soon'}
              {metrics.calibration_status === 'overdue' && '✗ Overdue'}
            </div>
            {metrics.calibration_next_due_date && (
              <div className="maintenance-date">Next: {formatDate(metrics.calibration_next_due_date)}</div>
            )}
          </div>

          {/* Warranty Status */}
          <div className="maintenance-card">
            <div className="maintenance-label">Warranty Status</div>
            <div className="maintenance-status" style={{ color: getStatusColor(metrics.warranty_status) }}>
              {metrics.warranty_status === 'active' && '✓ Active'}
              {metrics.warranty_status === 'expired' && '✗ Expired'}
            </div>
            {metrics.warranty_expiry_date && (
              <div className="maintenance-date">Expires: {formatDate(metrics.warranty_expiry_date)}</div>
            )}
          </div>

          {/* Contract Status */}
          <div className="maintenance-card">
            <div className="maintenance-label">Contract Status</div>
            <div className="maintenance-status" style={{ color: getStatusColor(metrics.contract_status) }}>
              {metrics.contract_status === 'active' && '✓ Active'}
              {metrics.contract_status === 'expired' && '✗ Expired'}
            </div>
            {metrics.contract_expiry_date && (
              <div className="maintenance-date">Expires: {formatDate(metrics.contract_expiry_date)}</div>
            )}
          </div>
        </div>
      </div>

      {/* Complaint Severity, Category & Trends */}
      {metrics.complaint_distribution && metrics.complaint_distribution.length > 0 && (
        <div className="reliability-section">
          <div className="reliability-section-header">
            <h4>Complaint Analysis</h4>
            <p className="reliability-section-desc">
              Severity and category breakdown with historical trends
              {metrics.observation_window?.label ? ` · ${metrics.observation_window.label}` : ''}
            </p>
          </div>
          <div className="complaint-analysis-grid">
            {/* Severity Cards */}
            <div className="severity-cards-container">
              <div className="severity-card critical">
                <div className="severity-icon">🔴</div>
                <div className="severity-content">
                  <div className="severity-label">Critical</div>
                  <div className="severity-value">{metrics.complaints_by_severity.critical}</div>
                </div>
              </div>
              <div className="severity-card major">
                <div className="severity-icon">🟠</div>
                <div className="severity-content">
                  <div className="severity-label">Major</div>
                  <div className="severity-value">{metrics.complaints_by_severity.major}</div>
                </div>
              </div>
              <div className="severity-card minor">
                <div className="severity-icon">🟡</div>
                <div className="severity-content">
                  <div className="severity-label">Minor</div>
                  <div className="severity-value">{metrics.complaints_by_severity.minor}</div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="charts-row">
              {/* By Category */}
              <div className="chart-wrapper">
                <h5 className="chart-title">By Category</h5>
                {(metrics.complaints_by_category || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={(metrics.complaints_by_category || []).map((item) => ({
                        name: item.label || 'Uncategorized',
                        count: item.count || 0,
                      }))}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={100}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <Tooltip
                        contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }}
                      />
                      <Bar dataKey="count" name="Complaints" radius={[0, 6, 6, 0]}>
                        {(metrics.complaints_by_category || []).map((_, index) => (
                          <Cell
                            key={`category-bar-${index}`}
                            fill={CATEGORY_CHART_COLORS[index % CATEGORY_CHART_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="category-chart-empty">No complaints in this window</div>
                )}
              </div>

              {/* Complaint Trends Chart */}
              <div className="chart-wrapper">
                <h5 className="chart-title">Trends (Last 12 Months)</h5>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={metrics.complaint_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" />
                    <YAxis allowDecimals={false} />
                    <Tooltip contentStyle={{ background: 'white', border: '1px solid #e9ecef', borderRadius: '8px' }} />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="var(--dp-accent-primary)" strokeWidth={3} name="Complaints" dot={{ fill: 'var(--dp-accent-primary)', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complaint History — transfer review: all; device view: current-location operational only */}
      {showComplaintHistorySection && (
      <div className="reliability-section">
        <div className="reliability-section-header collapsible-header" onClick={() => setExpandComplaintHistory(!expandComplaintHistory)}>
          <div className="header-title-wrapper">
            <span className="collapse-icon">{expandComplaintHistory ? '▼' : '▶'}</span>
            <div className="header-content">
              <h4>Complaint History (Last 5)</h4>
              {hasActiveComplaints && metricsView === 'operational' && (
                <p className="section-header-hint">
                  {isTechnician
                    ? [
                        'Click a row for quick view',
                        `${assignedToMeActiveCount} assigned to you`,
                        otherActiveCount > 0 ? `${otherActiveCount} others on this device` : null,
                        `${activeCount} active now`,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : `Click a row for quick view · ${activeCount} active now`}
                </p>
              )}
            </div>
          </div>
          <span className="complaint-count-badge">{complaintHistoryRows.length}</span>
        </div>

        {expandComplaintHistory && (
        <div className="complaint-history-table expand-animation">
          {complaintHistoryRows.length > 0 ? (
            <table className="complaints-table-view">
              <thead>
                <tr>
                  <th className="col-id">#</th>
                  <th className="col-complaint-id">Complaint ID</th>
                  <th className="col-category">Category</th>
                  <th className="col-title">Title</th>
                  <th className="col-reported-by">Reported By</th>
                  <th className="col-assigned">Assigned To</th>
                  <th className="col-created">Reported On</th>
                  <th className="col-severity">Severity</th>
                  <th className="col-status">Status</th>
                </tr>
              </thead>
              <tbody>
                {complaintHistoryRows.map((complaint, index) => {
                  const assignment = getComplaintAssignmentMeta(complaint, user);
                  return (
                  <tr
                    key={complaint.id}
                    className={`complaint-row${assignment.kind === 'mine' ? ' complaint-row-mine' : ''}`}
                    onClick={() => openComplaintFromHistory(complaint)}
                  >
                    <td className="col-id">{index + 1}</td>
                    <td className="col-complaint-id">
                      <ComplaintIDBadge complaintId={complaint.complaint_identifier} size="small" />
                    </td>
                    <td className="col-category" title={complaint.category_label || ''}>
                      {complaint.category_label || '—'}
                    </td>
                    <td className="col-title" title={complaint.title}>
                      <span className="title-text">{complaint.title}</span>
                    </td>
                    <td className="col-reported-by">
                      <span className="reported-by-cell">
                        <span className="reported-by-name">
                          {complaint.reported_by_name ||
                            complaint.reporter_full_name ||
                            complaint.reporter_employee_id ||
                            '—'}
                        </span>
                        {complaint.is_authenticated_reporter === false ||
                        complaint.is_authenticated_reporter === 0 ? (
                          <span
                            className="reporter-verification-badge manual"
                            title="Manual: Reporter details were entered manually (not registered)"
                          >
                            Manual
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className="col-assigned">
                      {assignment.kind === 'mine' ? (
                        <span className="assignment-you" title="Assigned to you">
                          You
                        </span>
                      ) : assignment.kind === 'unassigned' ? (
                        <span className="assignment-unassigned-text" title="Unassigned">
                          Unassigned
                        </span>
                      ) : (
                        <span
                          className="assignment-name"
                          title={`Assigned to ${assignment.label}`}
                        >
                          {assignment.label}
                        </span>
                      )}
                    </td>
                    <td className="col-created">{formatDate(complaint.created_at)}</td>
                    <td className="col-severity">
                      <SeverityBadge severity={complaint.severity} size="small" />
                    </td>
                    <td className="col-status">
                      <StatusBadge status={complaint.status} size="small" />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="no-data-state">No complaints recorded for this device</div>
          )}
        </div>
        )}
      </div>
      )}

      {/* Risk Score Info Modal - Reusable Component */}
      <RiskScoreInfoModal isOpen={showRiskInfo} onClose={() => setShowRiskInfo(false)} />
      <DeviceReliabilityHelpModal isOpen={showReliabilityHelp} onClose={() => setShowReliabilityHelp(false)} />

      {/* Complaint View Modal */}
      {selectedComplaintId && (
        <ComplaintView
          complaintId={selectedComplaintId}
          token={token}
          user={user}
          onClose={closeComplaintModal}
          onUpdate={readOnlyReviewMode || selectedComplaintViewOnly ? undefined : fetchMetrics}
          readOnlyReviewMode={selectedComplaintViewOnly}
          viewOnlyMessage={
            selectedComplaintViewOnly && isTechnician && !readOnlyReviewMode
              ? 'Not assigned to you — view only. This complaint will not appear in your Complaints list.'
              : null
          }
        />
      )}
    </div>
  );
}

export default ReliabilityMetrics;
